/**
 * De Arcadehal: één venster met alle spelletjes als tegels.
 *
 * De vier spelletjes stonden los in het startmenu, en dat menu werd daarmee een
 * lijst van acht regels waarvan de helft een spelletje was. Nu is het één regel
 * naar de hal, en in de hal staat op elke tegel wat het spelletje is.
 * Zo blijft het menu een menu en heeft elk volgend spelletje een plek zonder dat
 * er weer een regel bij komt.
 *
 * Op de tegels stond jouw record. Dat is eruit: een tegel hoort je binnen te
 * lokken, en "14 zetten" doet dat niet. Je eigen record staat in het spelletje
 * zelf, op de plek waar het iets betekent. In plaats daarvan draagt elke tegel
 * dezelfde Nieuw!-pil als de regel Arcadehal in het startmenu, in hetzelfde
 * paars, zodat het één ding is dat op twee plekken hetzelfde zegt.
 */

import { MAINTAINER_ID } from '../lib/config.js';

export default {
  openArcade() {
      this.vensterNaarVoren('arcade');
      this.setState({ arcadeOpen: true, vensterMin: Object.assign({}, this.state.vensterMin, { arcade: false }), notifOpen: false, startOpen: false });
    },

  closeArcade() { return this.setState({ arcadeOpen: false, vensterMin: Object.assign({}, this.state.vensterMin, { arcade: false }) }); },

  /**
   * Mag jij de spelletjes die nog in beta staan?
   *
   * De gastenlijst stond in de code, als een lijst met twee id's, en staat nu op de server: een
   * ja/nee-veld `beta` op je eigen regel in paal_accounts, dat de mod aanvinkt in
   * het modgereedschap. Zo hoeft er voor een tester erbij geen nieuwe versie de
   * lucht in.
   *
   * De mod komt er altijd in, ook zonder vinkje. Anders kan hij zichzelf buitensluiten
   * uit het scherm waarmee hij de vinkjes zet, en dat is een deur die alleen van
   * binnenuit opengaat.
   */
  magBeta() {
      if (this.isMaintainer) return true;
      const mij = (this.state.accounts || []).find((a) => a.id === this.state.accountId);
      return !!(mij && mij.beta);
    },

  /** Is deze persoon tester? Voor de kiezer in de Krabbellijn. */
  isBetaTester(id) {
      if (!id) return false;
      if (id === MAINTAINER_ID) return true;
      const a = (this.state.accounts || []).find((x) => x.id === id);
      return !!(a && a.beta);
    },

  /**
   * Eén melding voor elk spel dat nog in beta staat.
   *
   * Hij hangt aan de poort en niet aan de tegel: de tegel is al uitgeschakeld, maar
   * het adres in de balk (#krabbellijn) en de knop in de taakbalk
   * komen hier ook langs.
   */
  betaAf(spel) {
      this.notify(spel + ' staat nog in beta. Vraag g3rt om je aan te vinken als tester.');
    },

  arcadeVals() {
      const mob = this.state.isMobile;
      const ink = 'var(--ink)';
      /**
       * Een tegel. `uit` maakt hem onaanklikbaar: hij staat er wel, maar het spel
       * is nog niet voor iedereen open. Dat is een echte disabled-knop en niet een
       * knop die niets doet, want dan weet je toetsenbord en je schermlezer het ook.
       * De pil zegt dan waarom hij niet reageert.
       */
      const tegel = (icoon, titel, regel, onClick, uit, badge) => ({
        icoon, titel, regel, uit: !!uit,
        onClick: uit ? null : onClick,
        // De pil zegt wat het spel is, niet of jij erin mag: een spel in beta heet
        // Beta, of je er nu bij hoort of niet. Dat je niet kunt klikken zie je aan
        // de tegel zelf, die op veertig procent staat.
        badge: badge || 'Nieuw!',
        // Vierkant, en de inhoud van boven naar onder uitgesmeerd: icoon en naam
        // bovenaan, het regeltje in het midden, de pil onderaan.
        style: {
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
          justifyContent: 'space-between', gap: '6px',
          width: '100%', aspectRatio: '1', height: 'auto', textAlign: 'left', boxSizing: 'border-box',
          padding: '11px', borderRadius: '16px', background: 'var(--lila-diep)',
          fontWeight: 600
        },
        vakStyle: {
          width: '38px', height: '38px', flex: '0 0 auto',
          border: '3px solid ' + ink, borderRadius: '11px', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', lineHeight: 1
        },
        titelStyle: { fontSize: '14px', fontWeight: 700, lineHeight: 1.2 },
        // Hoogstens drie regels; in een vierkante tegel is er niet meer plek en
        // een tegel die uitrekt maakt de rij ongelijk.
        regelStyle: {
          fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)', lineHeight: 1.3,
          display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden'
        },
        // Dezelfde pil als bij de regel Arcadehal in het startmenu: paars met wit,
        // 19 hoog en een rand van twee. Zie startItemBadgeStyle in logic/desktop.js;
        // veranderen die twee niet samen, dan zeggen ze hetzelfde in twee kleuren.
        titelAttr: uit ? titel + ' is nog in beta' : 'Speel ' + titel,
        badgeStyle: {
          flex: '0 0 auto', height: '19px', padding: '0 8px', borderRadius: '12px',
          background: 'var(--paars)', color: '#fff', border: '2px solid ' + ink,
          fontSize: '11px', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }
      });
      return {
        showArcade: this.vensterInfo('arcade').open,
        openArcade: this.openArcade,
        closeArcade: this.closeArcade,
        // Wit kader: de hal is een blad met tegels en geen apparaatje.
        arcadeVenster: this.vensterVals('arcade', mob ? '100%' : 'min(640px,100%)',
          mob ? 'calc(100dvh - 20px)' : '90dvh', { vlak: '#fff', vol: true, rand: mob ? 18 : 24 }),
        arcadeTitel: 'Arcadehal',
        arcadeTitelStyle: {
          fontSize: mob ? '17px' : '19px', fontWeight: 700, letterSpacing: '-.01em', flex: '1 1 auto'
        },
        arcadePanelStyle: {
          // Van 560 naar 640 toen het vierde spel erbij kwam. Zie het raster
          // hieronder: op 560 wordt een tegel 119 breed, en daar blijft na de padding
          // 97 van over. De langste naam is Krabbelgotchi: nagemeten in het
          // lettertype zelf 89 pixels op 14px/700, dus acht over. Dat is te weinig om
          // op te bouwen; één spel met een langere naam en het loopt eruit. Op 640 is
          // de tegel 139 en blijft er 28 over.
          //
          // Meet dit opnieuw als er een spel bij komt of een naam verandert. Het
          // stond hier eerst met 95 pixels voor Krabbelbeestje, en dat getal was
          // achterhaald zodra het spel Krabbelgotchi ging heten.
          // Alleen nog de binnenruimte: rand, schaduw, kleur en breedte komen van het
          // venster. De breedte hierboven staat nu in arcadeVenster.
          flex: '1 1 auto', minHeight: '0',
          overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain',
          padding: mob ? '18px' : '24px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
        },
        // Vier vierkante tegels op één rij, sinds er een vierde spel is. Op drie
        // kolommen bleef het vierde spel alleen op een tweede rij staan, en dat
        // leest als een vergeten tegel in plaats van als een rij.
        //
        // Op een telefoon twee, dus twee bij twee: daar is een tegel van een derde
        // van 355 pixels al te klein voor een naam als Krabbellijn.
        //
        // Komt er een vijfde spel bij, dan sta je weer voor deze keuze. Dan is het
        // raster de plek om aan te draaien en niet de vensterbreedte: bij vijf
        // tegels naast elkaar wordt de tegel smaller dan de langste naam.
        arcadeRasterStyle: {
          display: 'grid', gridTemplateColumns: 'repeat(' + (mob ? 2 : 4) + ', minmax(0,1fr))',
          gap: '10px', minWidth: '0'
        },
        arcadeSpellen: [
          tegel('🐍', 'Krabbelworm', 'Eet je vol en word te lang voor je eigen veld.', this.openSnake),
          tegel('🃏', 'Krabbelry', 'Twintig kaartjes met de krabbels van de paal.', this.openMemory),
          // De Krabbelgotchi stond hier niet: het spel was nog niet goed genoeg en hing
          // in de wachtkamer. Nu staat het er weer, met dezelfde pil als de
          // Krabbellijn. Zie logic/tamagotchi.js en lib/tamagotchi.js.
          // Een space invader: het retro-beestje bij uitstek, en het is er ook echt
          // een. Er stond een eitje, en dat is maar de eerste minuut van het spel
          // waar. Zes van de acht tekentjes hiernaast zijn geen wezen maar een ding
          // (kaartjes, een hoorn); dit is er een met ogen, en dat hoort bij een
          // beestje dat je voert.
          // Uit de beta: deze tegel doet het voor iedereen, met of zonder vinkje.
          // Zonder account leeft je beestje alleen in je eigen browser, want
          // `tamStuur` schrijft niets weg zonder accountId; er komen dus geen regels
          // zonder eigenaar op de server te staan. Zie logic/tamagotchi.js.
          tegel('👾', 'Krabbelgotchi', 'Voer het, aai het en speel met het beestje uit je eigen krabbels.',
            this.openTamagotchi),
          // Krabbellijn staat in beta. De tegel staat er voor iedereen met "Beta"
          // erop, maar reageert alleen voor wie als tester is aangevinkt; zie magBeta.
          // Zo zie je dat hij komt, zonder halve ketens bij mensen die er nog niet
          // in kunnen.
          //
          // Een telefoonhoorn, dezelfde als in de taakbalk. Hier stond een schakel,
          // en dan had één spel twee beelden: een ketting in de hal en een telefoon
          // in de balk. De hoorn was er eerder al eens, als rest van de oude naam
          // Krabbeltelefoon; hij is terug omdat hij het spel het beste zegt en
          // omdat hij klein nog leesbaar is.
          tegel('\uD83D\uDCDE', 'Krabbellijn', 'Teken de zin van een ander en laat hem raden wat het was.',
            this.openKetting, !this.magBeta(), 'Beta')
        ]
      };
    }
};
